import Siema from "siema";

import LazyLoad from "./LazyLoad";
import NotificationAddToCart from "./AddToCart/NotificationAddToCart";
import ViewCartAddToCart from "./AddToCart/ViewCartAddToCart";

export default class FixedWidthCarousel extends Siema {
  get lastSlide() {
    return Math.max(0, this.totalSlides - this.perPage);
  }

  get perPage() {
    return Math.floor(this.selector.offsetWidth / this.slideWidth);
  }

  // Siema tries to set this property.
  /* eslint class-methods-use-this: 0 */

  set perPage(v) {}

  get selectorWidth() {
    return this.slideWidth * this.perPage;
  }

  // Siema tries to set this property.

  set selectorWidth(v) {}

  get maxOffset() {
    return (
      this.totalSlides * this.slideWidth -
      this.selector.offsetWidth -
      (this.config.borderWidth || 2)
    );
  }

  get shouldNotSlide() {
    return this.totalSlides <= this.perPage;
  }

  static bounds(val, min, max) {
    return Math.min(Math.max(min, val), max);
  }

  init() {
    if (this.selector.classList.contains("init")) return;
    if (!this.selector.querySelector(".ag-item")) return;

    this.slideWidth =
      this.config.slideWidth ||
      this.selector.querySelector(".ag-item").getBoundingClientRect().width;
    this.sigmoidMax = this.config.sigmoidMax || 120;
    this.sigmoidFactor = 300;
    this.totalSlides = this.innerElements.length;
    this.selectorContainer = this.selector.closest(
      "[data-component=carousel], .basic-carousel",
    );
    this.prevButton = this.selectorContainer.querySelector(
      "[data-control=prev]",
    );
    this.nextButton = this.selectorContainer.querySelector(
      "[data-control=next]",
    );

    super.init();

    // stopPropagation on mouseup events as Siema causes
    // the carousel to move back 1 page on Windows only
    this.selector.querySelectorAll("select").forEach((select) => {
      select.addEventListener("mouseup", (e) => {
        e.stopPropagation();
      });
    });

    this.setContainerWidth();
    this.showHideNavControls();

    const isStorefrontRendered =
      document.querySelector("[data-component=storefront-expanded-brand]") ||
      document.querySelector("[data-component=storefront-product-detail]");
    if (!isStorefrontRendered) {
      const viewCartExists = document.querySelector("body.cart");
      if (viewCartExists) {
        this.innerElements.forEach((element) => new ViewCartAddToCart(element));
      } else {
        this.innerElements.forEach(
          (element) => new NotificationAddToCart(element),
        );
      }
    }

    this.selector.classList.add("init");
    this.selectorContainer.classList.add("init");
    this.sliderFrame.classList.add("ag-carousel__frame");

    document.addEventListener("closeSideBySide", () => {
      this.resizeHandler();
    });
  }

  setContainerWidth() {
    this.selector.firstElementChild.style.width = `${
      this.totalSlides * this.slideWidth
    }px`;
  }

  showHideNavControls() {
    if (!this.prevButton || !this.nextButton) return;

    this.prevButton.hidden = false;
    this.nextButton.hidden = false;

    if (this.currentSlide <= 0 || this.shouldNotSlide) {
      this.prevButton.hidden = true;
    }
    if (
      this.currentSlide === this.totalSlides - this.perPage ||
      this.shouldNotSlide
    ) {
      this.nextButton.hidden = true;
    }

    if (this.isEventBoundToNavControls) return;

    this.prevButton.addEventListener("click", () => {
      this.prev(this.perPage);
    });
    this.nextButton.addEventListener("click", () => {
      this.next(this.perPage);
    });
    this.isEventBoundToNavControls = true;
  }

  resizeHandler() {
    super.resizeHandler();

    this.setContainerWidth();
    this.slideToCurrent();
    this.showHideNavControls();
  }

  sigmoid(num) {
    const value =
      this.sigmoidMax * (2 / (1 + Math.E ** -(num / this.sigmoidFactor)) - 1);
    return parseFloat(value.toFixed(3));
  }

  mousedownHandler(e) {
    this.drag.startX = e.pageX;

    super.mousedownHandler(e);
  }

  mousemoveHandler(e) {
    this.move(e);
  }

  touchmoveHandler(e) {
    this.move(e);
  }

  move(e) {
    const drag = {
      endX: e.pageX,
      endY: e.pageY,
    };

    if (e.touches && e.touches.length) {
      drag.endX = drag.endX || e.touches[0].pageX;
      drag.endY = drag.endY || e.touches[0].pageY;

      const dragDifferenceX = this.drag.startX - drag.endX;
      const dragDifferenceY = this.drag.startY - drag.endY;

      if (!this.dragDirection) {
        if (Math.abs(dragDifferenceX) > Math.abs(dragDifferenceY)) {
          this.dragDirection = "x";
        } else {
          this.dragDirection = "y";
        }
      }

      if (this.dragDirection === "x") {
        e.preventDefault();
      } else {
        return;
      }
    }

    this.drag.endX = drag.endX;
    this.drag.endY = drag.endY;

    if (
      this.shouldNotSlide ||
      !this.pointerDown ||
      this.drag.endX === this.drag.startX
    )
      return;
    // if dragged element is a link
    // mark preventClick prop as a true
    // to detemine about browser redirection later on
    const closestLink = e.target.closest("a") || document.createElement("div");
    if (
      closestLink.nodeName === "A" &&
      window?.document &&
      window.innerWidth >= 946
    ) {
      this.drag.preventClick = true;
    }

    this.selector.style.cursor = "-webkit-grabbing";
    this.sliderFrame.style.webkitTransition = `all 0ms ${this.config.easing}`;
    this.sliderFrame.style.transition = `all 0ms ${this.config.easing}`;
    const dragOffset = this.drag.endX - this.drag.startX;
    let offset = this.currentOffset - dragOffset;

    if (offset <= 0) {
      offset = this.sigmoid(offset);
    } else if (offset >= this.maxOffset) {
      offset = this.maxOffset + this.sigmoid(offset - this.maxOffset);
    }

    this.sliderFrame.style[this.transformProperty] = `translate3d(${
      -1 * offset
    }px, 0, 0)`;
  }

  clearDrag() {
    this.dragDirection = null;

    super.clearDrag();
  }

  slideToCurrent() {
    const signModifier = -1;
    const offsetAbsolute = FixedWidthCarousel.bounds(
      this.currentSlide * this.slideWidth,
      0,
      this.maxOffset,
    );
    let offset;

    if (
      this.currentSlide === this.lastSlide &&
      this.totalSlides > this.perPage
    ) {
      offset = this.maxOffset;
    } else if (this.shouldNotSlide) {
      offset = 0;
    } else {
      offset = offsetAbsolute;
    }

    this.currentOffset = offset;

    offset *= signModifier;

    this.sliderFrame.style[this.transformProperty] =
      `translate3d(${offset}px, 0, 0)`;

    this.showHideNavControls();

    LazyLoad.getInvisible().revalidate();
    LazyLoad.getInstance().revalidate();
  }
}
